import { LoaderFunctionArgs, redirect } from '@remix-run/node';
import { Form, useLoaderData, useNavigation } from '@remix-run/react';
import { authenticator, RETURN_TO_SESSION_KEY, safeRedirectPath } from '~/services/auth.server';
import { getCanonicalOrigin, isPreviewHost } from '~/services/preview-auth.server';
import { commitSession, getSession } from '~/services/session.server';
import { getPageTitle } from '~/lib/meta';
import { Loader2 } from 'lucide-react';

export const handle = { emoji: '🔐' };

export const meta = () => {
  return [{ title: getPageTitle('Log in') }];
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const returnTo = url.searchParams.get('returnTo');

  // Google OAuth can only redirect back to the canonical host (exact-match
  // redirect URIs), so preview hosts cannot run the login flow themselves.
  // Bounce to canonical staging, which authenticates normally and hands a
  // short-lived token back to the preview (see preview-auth.server.ts).
  const requestHost = request.headers.get('host');
  if (isPreviewHost(requestHost)) {
    return redirect(
      `${getCanonicalOrigin()}/auth/preview-handoff?${new URLSearchParams({
        host: requestHost,
        ...(returnTo ? { returnTo } : {}),
      })}`
    );
  }

  const user = await authenticator.isAuthenticated(request);
  if (user) {
    return redirect(safeRedirectPath(returnTo));
  }

  const session = await getSession(request.headers.get('Cookie'));

  const error = session.get(authenticator.sessionErrorKey);
  session.unset(authenticator.sessionErrorKey);

  // Stash the requested destination so the OAuth callback can return the user
  // there after Google sign-in (query params don't survive the OAuth redirect).
  // Clear any stale value when arriving at a plain /login so an old returnTo
  // can't hijack a later, unrelated sign-in.
  if (returnTo) {
    session.set(RETURN_TO_SESSION_KEY, returnTo);
  } else {
    session.unset(RETURN_TO_SESSION_KEY);
  }

  return Response.json(
    { error },
    {
      headers: {
        'Set-Cookie': await commitSession(session),
      },
    }
  );
};

function formatError(error: unknown): string {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error !== null) {
    const err = error as Record<string, unknown>;
    if (err.message && typeof err.message === 'string') return err.message;
  }
  return 'Something went wrong. Please try again.';
}

export default function Login() {
  const { error } = useLoaderData<typeof loader>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';

  return (
    <div className="flex min-h-dvh items-center justify-center bg-gradient-to-br from-brand-950 via-brand-900 to-brand-950 p-4 relative overflow-hidden font-sans">
      <div className="absolute left-1/2 top-[35%] -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,hsl(var(--brand))_0%,transparent_70%)] pointer-events-none animate-breathe" />

      <div className="relative z-10 flex flex-col items-center w-full max-w-[380px]">
        <div className="animate-login-fade-in w-full rounded-2xl border border-brand-300/15 bg-brand-950/60 backdrop-blur-xl p-8 shadow-[0_20px_40px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(127,211,218,0.1)] transition-colors duration-300 hover:border-brand-300/30">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-white">Welcome back to hhOS</h2>
            <p className="text-sm text-brand-200/60 mt-1.5">Sign in to your account to continue</p>
          </div>

          <Form action="/auth/google" method="post">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex w-full items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm transition-all duration-150 hover:shadow-md hover:-translate-y-px active:scale-[0.98] active:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-300 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-sm"
            >
              {isSubmitting ? (
                <Loader2 className="size-5 shrink-0 animate-spin text-slate-400" />
              ) : (
                <svg className="size-5 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
              )}
              {isSubmitting ? 'Signing in...' : 'Sign in with Google'}
            </button>
          </Form>

          {error && (
            <div className="mt-4 px-4 py-3 rounded-lg border border-red-500/30 bg-red-950/30 text-red-300 text-sm">
              {formatError(error)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
